import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Mail, CalendarPlus, CheckCircle2, UserCog, Download,
  Plus, Loader2, History, FileText, AlertOctagon, ShieldOff, HandCoins,
  CalendarClock, Trash2, ListChecks,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  fmtXof, fmtDate, bucketOf, BUCKET_META, PAYMENT_STATUS_FR, initials, avatarColor,
} from './recMeta';
import { RecordPaymentModal } from './RecordPaymentModal';
import { RelanceModal } from './RelanceModal';
import { DunningTaskModal } from './DunningTaskModal';
import { DunningMoreActions } from './DunningMoreActions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REC_API = `${API}/admin/recouvrement`;

const Row = ({ label, children, testid }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-right text-sm font-medium text-gray-800" data-testid={testid}>{children}</span>
  </div>
);

const ActionBtn = ({ icon: Icon, label, onClick, tone = 'default', disabled, testid }) => {
  const tones = {
    default: 'border-gray-200 text-gray-700 hover:bg-gray-50',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    red: 'border-red-200 text-red-600 hover:bg-red-50',
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border p-3 text-[11px] font-medium transition-colors disabled:opacity-50 ${tones[tone]}`}>
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
};

export const InvoiceDrawer = ({ invoiceRef, commercials = [], onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState('');
  const [reassignScope, setReassignScope] = useState('operation'); // 'operation' | 'client'
  const [reassignReason, setReassignReason] = useState('Réassignation recouvrement');
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [relance, setRelance] = useState(null); // 'email' | 'whatsapp'
  const [taskOpen, setTaskOpen] = useState(false);

  const loadCase = useCallback(async () => {
    try {
      const { data } = await axios.get(`${REC_API}/invoice/${encodeURIComponent(invoiceRef)}/case`);
      setCaseData(data);
    } catch {
      setCaseData(null);
    }
  }, [invoiceRef]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/commercials/invoice-detail`, { params: { ref: invoiceRef } });
      setDetail({ ...data, invoiceRef });
      loadCase();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Détail facture introuvable');
      onClose();
    } finally { setLoading(false); }
  }, [invoiceRef, onClose, loadCase]);

  useEffect(() => { load(); }, [load]);

  const openPayModal = () => {
    if (!detail) return;
    if ((detail.amountDue || 0) <= 0) {
      toast.info('Cette facture est déjà entièrement soldée.');
      return;
    }
    setPayModalOpen(true);
  };

  // Ouvre le panneau de réassignation en pré-sélectionnant la portée éligible.
  const openReassignPanel = () => {
    const elig = detail?.reassignEligibility || {};
    // Par défaut : opération si éligible, sinon client si éligible, sinon opération (message d'aide affiché).
    if (elig.operation?.enabled) setReassignScope('operation');
    else if (elig.client?.enabled) setReassignScope('client');
    setReassignOpen((o) => !o);
  };

  const relanceEmail = () => setRelance('email');

  const reassign = async () => {
    if (!reassignTo) return;
    const elig = detail?.reassignEligibility || {};
    if (reassignScope === 'operation' && !elig.operation?.enabled) {
      toast.error(elig.operation?.reason || "Réassignation par opération impossible.");
      return;
    }
    if (reassignScope === 'client' && !elig.client?.enabled) {
      toast.error(elig.client?.reason || "Réassignation client impossible pour cette facture.");
      return;
    }
    const reason = (reassignReason || '').trim();
    if (reason.length < 3) {
      toast.error('Merci de préciser un motif (au moins 3 caractères).');
      return;
    }
    setBusy(true);
    try {
      if (reassignScope === 'operation') {
        await axios.patch(
          `${API}/admin/operations/${detail.operationId}/commercial`,
          { commercialId: reassignTo, reason }
        );
        toast.success('Opération réassignée');
      } else {
        await axios.patch(
          `${API}/admin/companies/${detail.clientId}/commercial`,
          { commercialId: reassignTo, reason }
        );
        toast.success('Portefeuille client réassigné');
      }
      setReassignOpen(false); setReassignTo('');
      load(); onChanged && onChanged();
    } catch (e) {
      const backendMsg = e?.response?.data?.detail;
      // Message plus actionnable si backend renvoie "Client introuvable"
      if (backendMsg === 'Client introuvable' && detail?.operationId) {
        toast.error("Le compte client n'existe plus — utilisez « Cette opération seulement ».");
      } else {
        toast.error(backendMsg || 'Échec de la réassignation');
      }
    } finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${REC_API}/invoice/${encodeURIComponent(invoiceRef)}/pdf`,
        { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${detail?.invoiceNumber || invoiceRef}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Facture téléchargée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF de facture indisponible');
    } finally { setBusy(false); }
  };

  const setTaskStatus = async (taskId, status) => {
    try {
      await axios.patch(`${REC_API}/tasks/${taskId}`, { status });
      toast.success(status === 'done' ? 'Tâche terminée' : 'Tâche annulée');
      loadCase();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mise à jour impossible');
    }
  };

  const cancelScheduled = async (id) => {
    try {
      await axios.delete(`${REC_API}/scheduled-reminders/${id}`);
      toast.success('Relance planifiée annulée');
      loadCase();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Annulation impossible');
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/commercials/invoice-notes`, { invoiceRef, note: note.trim() });
      setNote('');
      toast.success('Note ajoutée');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec ajout note');
    } finally { setBusy(false); }
  };

  const bk = detail ? bucketOf(detail.daysOverdue) : '0-30';

  return (
    <>
      {/* Backdrop (mobile/tablette uniquement) */}
      <div className="fixed inset-0 z-40 bg-black/30 xl:hidden" onClick={onClose} data-testid="rec-drawer-backdrop" />
      <aside
        className="fixed right-0 top-16 bottom-0 z-50 w-full overflow-y-auto border-l border-gray-200 bg-white shadow-xl sm:w-[440px] sm:top-20 xl:shadow-lg"
        data-testid="rec-invoice-drawer"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <div className="text-xs text-gray-500">Facture</div>
            <div className="font-mono text-base font-bold text-gray-900">{invoiceRef}</div>
          </div>
          <div className="flex items-center gap-2">
            {detail && <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ${BUCKET_META[bk].chip}`}>{BUCKET_META[bk].label}</span>}
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="rec-drawer-close"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {loading || !detail ? (
          <div className="space-y-3 p-5">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}</div>
        ) : (
          <div className="px-5 py-4">
            <div className="divide-y divide-gray-50">
              <Row label="Client" testid="rec-drawer-client">{detail.clientName}</Row>
              <Row label="Commercial">
                {detail.commercialName ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold text-white ${avatarColor(detail.commercialId || detail.commercialName)}`}>{initials(detail.commercialName)}</span>
                    {detail.commercialName}
                  </span>
                ) : <span className="text-amber-600">Non assigné</span>}
              </Row>
              <Row label="Montant total"><span className="font-semibold text-gray-800">{fmtXof(detail.amount)}</span></Row>
              {(detail.amountPaid || 0) > 0 && (
                <Row label="Déjà encaissé" testid="rec-drawer-paid">
                  <span className="font-semibold text-emerald-700">{fmtXof(detail.amountPaid)}</span>
                </Row>
              )}
              <Row label="Restant dû" testid="rec-drawer-due">
                <span className="text-base font-bold text-gray-900">{fmtXof(detail.amountDue)}</span>
              </Row>
              <Row label="Date d'émission">{fmtDate(detail.issueDate)}</Row>
              <Row label="Date d'échéance">{fmtDate(detail.dueDate)}</Row>
              <Row label="Retard"><span className={detail.daysOverdue > 0 ? 'text-red-600' : 'text-gray-600'}>{detail.daysOverdue > 0 ? `${detail.daysOverdue} jours` : 'À jour'}</span></Row>
              <Row label="Relances">{detail.reminderCount || 0}</Row>
              <Row label="Statut"><span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ${BUCKET_META[bk].chip}`}>{PAYMENT_STATUS_FR[detail.paymentStatus] || detail.paymentStatus}</span></Row>
            </div>

            {/* Opération liée */}
            {detail.invoiceType === 'operation' && (
              <div className="mt-5">
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Opération liée</h3>
                <div className="divide-y divide-gray-50">
                  <Row label="N° opération"><span className="font-mono text-xs">{detail.operationNumber}</span></Row>
                  <Row label="Trajet">{detail.route}</Row>
                  <Row label="Statut opération"><span className="inline-flex rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">{detail.opStatusLabel}</span></Row>
                  <Row label="Date de livraison">{fmtDate(detail.deliveryDate)}</Row>
                </div>
              </div>
            )}
            {detail.invoiceType === 'contract' && detail.contractNumber && (
              <div className="mt-5">
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Contrat lié</h3>
                <div className="divide-y divide-gray-50"><Row label="N° contrat"><span className="font-mono text-xs">{detail.contractNumber}</span></Row></div>
              </div>
            )}

            {/* Bandeaux d'état du dossier */}
            {caseData?.writeOff && (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-800" data-testid="rec-banner-writeoff">
                <ShieldOff className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-semibold">Créance passée en perte</p>
                  <p className="mt-0.5">{caseData.writeOff.reason} · {caseData.writeOff.decidedBy} · {fmtDate(caseData.writeOff.decidedAt)}</p>
                </div>
              </div>
            )}
            {caseData?.dispute && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 text-xs text-orange-800" data-testid="rec-banner-dispute">
                <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-semibold">Facture en litige</p>
                  <p className="mt-0.5">{caseData.dispute.reason} · ouvert par {caseData.dispute.openedBy}</p>
                </div>
              </div>
            )}
            {caseData?.promise && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900" data-testid="rec-banner-promise">
                <HandCoins className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-semibold">Promesse de paiement : {fmtXof(caseData.promise.amount)} le {fmtDate(caseData.promise.promiseDate)}</p>
                  {caseData.promise.note && <p className="mt-0.5">{caseData.promise.note}</p>}
                </div>
              </div>
            )}

            {/* Tâches de suivi */}
            <div className="mt-5">
              <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                <ListChecks className="h-4 w-4 text-gray-400" /> Tâches de suivi
                {(caseData?.openTasks || []).length > 0 && (
                  <span className="rounded-full bg-blue-100 px-1.5 text-[10px] font-bold text-blue-700">
                    {caseData.openTasks.length}
                  </span>
                )}
              </h3>
              {(caseData?.tasks || []).length === 0 ? (
                <div className="rounded-lg bg-gray-50 px-3 py-3 text-xs text-gray-400">Aucune tâche sur cette facture.</div>
              ) : (
                <ul className="space-y-1.5" data-testid="rec-task-list">
                  {caseData.tasks.map((t) => (
                    <li key={t.id} className={`rounded-lg px-3 py-2 text-xs ${t.status === 'open' ? 'bg-blue-50' : 'bg-gray-50 opacity-70'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className={`font-medium ${t.status === 'done' ? 'text-gray-500 line-through' : 'text-gray-800'}`}>{t.title}</p>
                          <p className="mt-0.5 text-[10px] text-gray-500">
                            {t.assigneeName} · {t.dueDate} {t.dueTime} · priorité {t.priority}
                          </p>
                        </div>
                        {t.status === 'open' && (
                          <div className="flex shrink-0 gap-1">
                            <button type="button" title="Terminer" onClick={() => setTaskStatus(t.id, 'done')}
                              className="rounded p-1 text-emerald-600 hover:bg-emerald-100" data-testid={`rec-task-done-${t.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </button>
                            <button type="button" title="Annuler" onClick={() => setTaskStatus(t.id, 'cancelled')}
                              className="rounded p-1 text-gray-400 hover:bg-gray-200" data-testid={`rec-task-cancel-${t.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Relances planifiées */}
            {(caseData?.scheduledReminders || []).length > 0 && (
              <div className="mt-5">
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                  <CalendarClock className="h-4 w-4 text-gray-400" /> Relances planifiées
                </h3>
                <ul className="space-y-1.5" data-testid="rec-scheduled-list">
                  {caseData.scheduledReminders.map((s) => (
                    <li key={s.id} className="flex items-center justify-between rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-900">
                      <span>{s.channel === 'email' ? 'Email' : 'WhatsApp'} le {fmtDate(s.scheduledFor)}</span>
                      <button type="button" onClick={() => cancelScheduled(s.id)}
                        className="rounded p-1 text-blue-500 hover:bg-blue-100" data-testid={`rec-scheduled-cancel-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Historique des relances */}
            <div className="mt-5">
              <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-900"><History className="h-4 w-4 text-gray-400" /> Historique des relances</h3>
              {(detail.reminderHistory || []).length === 0 ? (
                <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-3 text-xs text-gray-400"><FileText className="h-4 w-4" /> Aucune relance effectuée</div>
              ) : (
                <ul className="space-y-1.5">
                  {detail.reminderHistory.map((h, i) => (
                    <li key={i} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-xs">
                      <span className="text-gray-700">{h.type === 'reminder' ? `Relance niv. ${h.level ?? '—'}` : (h.result || 'Action')} · {h.channel || 'email'}</span>
                      <span className="text-gray-400">{fmtDate(h.date)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Notes internes */}
            <div className="mt-5">
              <h3 className="mb-2 text-sm font-semibold text-gray-900">Notes internes</h3>
              {(detail.notes || []).length === 0 ? (
                <div className="rounded-lg bg-gray-50 px-3 py-3 text-xs text-gray-400">Aucune note pour le moment.</div>
              ) : (
                <ul className="mb-2 space-y-1.5">
                  {detail.notes.map((n) => (
                    <li key={n.id} className="rounded-lg bg-gray-50 px-3 py-2 text-xs">
                      <div className="text-gray-700">{n.note}</div>
                      <div className="mt-0.5 text-[10px] text-gray-400">{n.authorName} · {fmtDate(n.createdAt)}</div>
                    </li>
                  ))}
                </ul>
              )}
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ajouter une note interne…" className="text-xs" data-testid="rec-note-input" />
              <Button size="sm" variant="outline" className="mt-2 h-8 text-xs" onClick={addNote} disabled={busy || !note.trim()} data-testid="rec-note-add">
                <Plus className="mr-1 h-3.5 w-3.5" /> Ajouter une note
              </Button>
            </div>

            {/* Réassignation inline */}
            {reassignOpen && (
              <div className="mt-4 space-y-2.5 rounded-lg border border-gray-200 p-3" data-testid="rec-reassign-panel">
                <div className="text-xs font-medium text-gray-700">Réassigner à un commercial</div>

                {/* Portée — basée sur reassignEligibility du backend */}
                {!detail.reassignEligibility?.operation?.enabled && !detail.reassignEligibility?.client?.enabled && (
                  <div className="rounded-md border border-yellow-200 bg-yellow-50 px-2.5 py-1.5 text-[11px] text-yellow-800" data-testid="rec-reassign-blocked">
                    Aucune réassignation possible pour cette facture — données client manquantes ou opération sans référence.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setReassignScope('operation')}
                    disabled={!detail.reassignEligibility?.operation?.enabled}
                    className={`rounded-md border p-2 text-left text-[11px] transition-colors ${
                      reassignScope === 'operation'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    } ${!detail.reassignEligibility?.operation?.enabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                    data-testid="rec-reassign-scope-operation"
                    title={detail.reassignEligibility?.operation?.reason || ''}
                  >
                    <div className="font-semibold">Cette opération seulement</div>
                    <div className="mt-0.5 text-[10px] text-gray-500">
                      {detail.reassignEligibility?.operation?.enabled
                        ? "Recommandé — n'affecte pas le portefeuille client"
                        : (detail.reassignEligibility?.operation?.reason || 'Non éligible')}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setReassignScope('client')}
                    disabled={!detail.reassignEligibility?.client?.enabled}
                    className={`rounded-md border p-2 text-left text-[11px] transition-colors ${
                      reassignScope === 'client'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    } ${!detail.reassignEligibility?.client?.enabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                    data-testid="rec-reassign-scope-client"
                    title={detail.reassignEligibility?.client?.reason || ''}
                  >
                    <div className="font-semibold">Tout le portefeuille client</div>
                    <div className="mt-0.5 text-[10px] text-gray-500">
                      {detail.reassignEligibility?.client?.enabled
                        ? 'Cascade sur toutes ses opérations'
                        : (detail.reassignEligibility?.client?.reason || 'Non éligible')}
                    </div>
                  </button>
                </div>

                {/* Commercial */}
                <Select value={reassignTo} onValueChange={setReassignTo}>
                  <SelectTrigger className="h-8 text-xs" data-testid="rec-reassign-select">
                    <SelectValue placeholder="Choisir un commercial…" />
                  </SelectTrigger>
                  <SelectContent>
                    {commercials.map((c) => (
                      <SelectItem key={c.commercialId || c.id} value={c.commercialId || c.id}>
                        {c.commercialName || c.fullName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Motif */}
                <Input
                  value={reassignReason}
                  onChange={(e) => setReassignReason(e.target.value)}
                  placeholder="Motif de la réassignation (obligatoire)"
                  className="h-8 text-xs"
                  data-testid="rec-reassign-reason"
                />

                <div className="flex items-center justify-end gap-2">
                  <Button
                    size="sm" variant="outline" className="h-8 text-xs"
                    onClick={() => setReassignOpen(false)}
                    data-testid="rec-reassign-cancel"
                  >
                    Annuler
                  </Button>
                  <Button
                    size="sm"
                    className="h-8 bg-emerald-600 text-xs hover:bg-emerald-700"
                    onClick={reassign}
                    disabled={busy || !reassignTo || (reassignReason || '').trim().length < 3}
                    data-testid="rec-reassign-confirm"
                  >
                    Confirmer
                  </Button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="mt-5">
              <h3 className="mb-2 text-sm font-semibold text-gray-900">Actions</h3>
              <div className="grid grid-cols-3 gap-2">
                <ActionBtn icon={Mail} label="Relancer par email" tone="green" onClick={relanceEmail} disabled={busy} testid="rec-action-email" />
                <ActionBtn icon={CalendarPlus} label="Créer une tâche" onClick={() => setTaskOpen(true)} testid="rec-action-task" />
                <ActionBtn icon={CheckCircle2} label="Enregistrer un paiement" onClick={openPayModal} disabled={busy || (detail.amountDue || 0) <= 0} testid="rec-action-paid" />
                <ActionBtn icon={UserCog} label="Réassigner" onClick={openReassignPanel} disabled={busy} testid="rec-action-reassign" />
                <ActionBtn icon={Download} label="Télécharger la facture" onClick={download} disabled={busy} testid="rec-action-download" />
                <DunningMoreActions
                  invoiceRef={invoiceRef}
                  detail={detail}
                  caseData={caseData}
                  onWhatsApp={() => setRelance('whatsapp')}
                  onChanged={() => { loadCase(); load(); onChanged && onChanged(); }}
                />
              </div>
            </div>
            {busy && <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Traitement…</div>}
          </div>
        )}
      </aside>
      {detail && payModalOpen && (
        <RecordPaymentModal
          open={payModalOpen}
          onClose={() => setPayModalOpen(false)}
          invoice={detail}
          onSuccess={() => { load(); onChanged && onChanged(); }}
        />
      )}
      {detail && relance && (
        <RelanceModal
          open={!!relance}
          invoiceRef={invoiceRef}
          channel={relance}
          onClose={() => setRelance(null)}
          onSent={() => { load(); onChanged && onChanged(); }}
        />
      )}
      {detail && taskOpen && (
        <DunningTaskModal
          open={taskOpen}
          invoiceRef={invoiceRef}
          clientName={detail.clientName}
          onClose={() => setTaskOpen(false)}
          onCreated={loadCase}
        />
      )}
    </>
  );
};

export default InvoiceDrawer;
